import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";
import { Popover } from "./Popover";
import { BookingCodeFormatHint } from "./BookingCodeFormatHint";
import { useBookingCodeStore } from "../stores/bookingCodeStore";
import { useBookingCodeCommit } from "./useBookingCodeCommit";

interface BookingCodeSearchSegmentProps {
  siteId: string;
  /** Current draft value (also committed on the unified search). */
  value: string;
  onChange: (value: string) => void;
  /**
   * Shared pill-state wiring from <BookingSearch />'s `afterGuests` render
   * prop. When provided, the popover is controlled by the host so this pill
   * highlights/dims in lockstep with the dates/guests pills; when omitted,
   * the popover manages itself and the pill renders in its resting style.
   */
  open?: boolean;
  otherActive?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Shared by the Apply and remove buttons: the min width keeps the popover from
 * resizing when one replaces the other. It has to clear the longest "Apply"
 * label (sv "Tillämpa"), which `px-4` alone does not guarantee against an
 * icon-only button.
 */
const ACTION_BUTTON_CLASSES =
  "flex h-9 min-w-[6.5rem] shrink-0 items-center justify-center rounded-full bg-primary px-4 text-sm font-medium text-on-primary transition-opacity";

/**
 * Desktop "Bokningskod" pill that lives in <BookingSearch />'s `afterGuests`
 * slot, between the guests pill and the search button. It mirrors the
 * dates/guests pills' styling and opens a small popover with a text input
 * and an "Apply" button (same semantics as the mobile block).
 *
 * Apply resolves the code into the store without running the unified search
 * commit — useSearch reacts to the store change and re-prices. There is no
 * separate "check" step: a code that doesn't resolve simply isn't applied.
 * The unified search commit (search icon / "Uppdatera") still reconciles the
 * draft as before, so committing without pressing Apply keeps working.
 *
 * The (i) inside the input explains the code format only. Rules live where the
 * applied code is shown — the badge's (i) on desktop, the inline card in the
 * mobile sheet — so they are deliberately absent from this popover.
 */
export const BookingCodeSearchSegment: React.FC<
  BookingCodeSearchSegmentProps
> = ({
  siteId,
  value,
  onChange,
  open,
  otherActive = false,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const commitBookingCode = useBookingCodeCommit(siteId);
  const applied = useBookingCodeStore((s) => s.applied);
  const appliedCode = useBookingCodeStore((s) => s.code);
  const [isApplying, setIsApplying] = useState(false);

  const label = t("booking_code.input_label");
  const displayValue = value.trim() ? value : t("booking_code.add_code");

  const trimmed = value.trim();
  const isAppliedDraft = applied && trimmed === (appliedCode ?? "");
  const canApply = trimmed.length > 0 && !isAppliedDraft && !isApplying;

  const handleApply = async () => {
    if (!canApply) return;
    setIsApplying(true);
    try {
      await commitBookingCode(value);
      // Close once the code is in the store: the pill then shows it, and the
      // prices behind the popover are what the guest wants to look at. Closing
      // unconditionally after the commit is correct even for a code that
      // resolved to nothing — non-disclosure means any typed code is applied.
      onOpenChange?.(false);
    } catch {
      // Caught rather than left to reject: callers fire this as `void
      // handleApply()`, so a failure was an unhandled rejection, and the popover
      // stayed open showing a code that had been applied nowhere. The mutation's
      // own error handler does the telling; the popover just closes either way,
      // because the state it would be waiting for is never coming.
      onOpenChange?.(false);
    } finally {
      setIsApplying(false);
    }
  };

  // Immediate removal — clears both the draft and the store so prices revert
  // without needing a search commit.
  // Routed through the commit hook rather than clearing the store directly: with
  // a cart already open, the reservation has to lose the agreement too, or the
  // pill reads "no code" while checkout still charges the discounted total.
  const handleClear = async () => {
    onChange("");
    try {
      await commitBookingCode("");
    } catch {
      // Wired straight to onClick, so a rejection would be unhandled. The
      // mutation's error handler reports it; the store keeps the code, which is
      // the truth while the reservation still carries it.
    }
  };

  // Mirrors BookingSearch's pillContainerClasses states so this pill styles
  // identically to the dates/guests pills in every popover combination.
  const pillStateClasses = open
    ? "bg-surface-card shadow-lg"
    : otherActive
      ? "bg-transparent"
      : "bg-transparent hover:bg-border";

  return (
    <div
      className={`flex-1 rounded-full transition-[background-color,box-shadow] duration-200 ease-out motion-reduce:transition-none ${pillStateClasses}`}
    >
      <Popover
        placement="bottom"
        offset={12}
        open={open}
        onOpenChange={onOpenChange}
        className="!min-w-[320px] !rounded-3xl !p-3"
        trigger={
          <button
            type="button"
            data-testid="booking-code-button"
            className="font-inter w-full !rounded-none !border-0 !p-0 text-left"
          >
            <div className="flex flex-col items-start px-4 py-3">
              <span className="text-xs font-medium text-ink-secondary">
                {label}
              </span>
              <span className="text-xs font-bold">{displayValue}</span>
            </div>
          </button>
        }
        content={
          <div
            data-testid="booking-code-popover"
            className="flex items-center gap-2 text-left"
          >
            <div className="relative flex-1">
              <input
                type="text"
                value={value}
                autoFocus
                disabled={isAppliedDraft}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void handleApply();
                  }
                }}
                placeholder={label}
                aria-label={label}
                data-testid="booking-code-input"
                className="h-9 w-full rounded-full border border-border bg-surface-card pl-3 pr-9 text-sm text-ink placeholder:text-ink/40 focus:outline-none disabled:bg-surface-hover disabled:text-ink/50"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <BookingCodeFormatHint openOn="hover" />
              </div>
            </div>
            {/* Applied: the slot removes the code instead of offering a dead
                disabled Apply. Both branches share a min width so swapping
                between them doesn't resize the popover. */}
            {isAppliedDraft ? (
              <button
                type="button"
                onClick={handleClear}
                aria-label={t("booking_code.clear")}
                data-testid="booking-code-clear"
                className={ACTION_BUTTON_CLASSES}
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void handleApply()}
                disabled={!canApply}
                data-testid="booking-code-apply"
                className={`${ACTION_BUTTON_CLASSES} disabled:opacity-40`}
              >
                {t("booking_code.apply")}
              </button>
            )}
          </div>
        }
      />
    </div>
  );
};
